import { useContext, useMemo } from 'react';
import { Filters, TimeFilter } from 'services/data.service';
import { IntlContext } from 'context/LocalisationContext';
import { TenantConfigContext, TenantConfigUtil } from 'context/TenantConfigContext';
import { calculateGranularity } from 'hooks/utils/calculateTimeGranularity';
import { formatDate } from 'utils/formatDate';
import { useAppStore } from 'hooks/state/useAppStore';

export interface ExportRequest {
  timeFilter: TimeFilter | null;
  filters: Filters;
  creationTimeFilter: TimeFilter | null;
  locale: string;
  currencySymbol: string;
}

export const useExportRequest = (): ExportRequest => {
  const {
    genders,
    ageGroups,
    seasons,
    fulfillmentMethods,
    dateBy,
    products,
    brands,
    countries,
    categories,
    marketplaces,
    dateRange,
    creationDateRange,
  } = useAppStore();

  const {
    currentConfig: { localeCode },
  } = useContext(IntlContext);
  const { currencyCode } = useContext(TenantConfigContext);

  // Validate date ranges before granularity calculation
  const validDateRange = dateRange?.range?.from && dateRange?.range?.to ? dateRange.range : null;
  const validCreationDateRange =
    creationDateRange?.range?.from && creationDateRange?.range?.to ? creationDateRange.range : null;

  const dateRangeGranularity = validDateRange ? calculateGranularity(validDateRange) : null;
  const creationRangeGranularity = validCreationDateRange ? calculateGranularity(validCreationDateRange) : null;

  return useMemo(
    () => ({
      locale: localeCode,
      currencySymbol: TenantConfigUtil.getCurrencySymbol(currencyCode),
      timeFilter:
        validDateRange && dateRangeGranularity
          ? {
              fromDate: formatDate(validDateRange.from!),
              toDate: formatDate(validDateRange.to!),
              granularity: dateRangeGranularity,
            }
          : null,
      creationTimeFilter:
        validCreationDateRange && creationRangeGranularity
          ? {
              fromDate: formatDate(validCreationDateRange.from!),
              toDate: formatDate(validCreationDateRange.to!),
              granularity: creationRangeGranularity,
            }
          : null,
      filters: {
        brands,
        countries,
        genders,
        ageGroups,
        seasons,
        fulfillmentMethods,
        categories,
        marketplaces,
        dateBy,
        productNumbers: products?.map(product => product),
      },
    }),
    [
      brands,
      countries,
      categories,
      genders,
      ageGroups,
      seasons,
      marketplaces,
      fulfillmentMethods,
      dateBy,
      products,
      validDateRange,
      localeCode,
      currencyCode,
      validCreationDateRange,
    ]
  );
};
